import type { Revenue } from '../types/finance';
import { sumarMontos } from './expenses';
import { cobradoDistintoDelPropuesto } from './finance';

/**
 * FE-32 — Métricas por RUTA (= tarifa con la que se cobró). El agregado del
 * servidor no tiene grano por tarifa (ADR-030 §5: cliente, vehículo,
 * sub-unidad, día), así que se arma aquí sobre el detalle de ingresos del
 * rango, igual que «gasto por conductor» sobre el detalle de gastos.
 *
 * Lo que mide es el lado del INGRESO: cuánto deja cada ruta, por viaje y por
 * unidad. El costo no se reparte por ruta: un gasto cuelga del vehículo, del
 * conductor o del viaje, nunca de una tarifa. Cuando todos los viajes tengan
 * sus gastos asociados por ventana (ADR-024), el margen por ruta saldrá de
 * cruzar ambos por `tripId`; hoy sería una cifra a medias y no se muestra.
 */
export interface RutaMetrica {
  key: string;
  /** Nombre de la tarifa (ruta, destino o servicio). */
  ruta: string;
  cliente: string;
  clienteId: string;
  unit?: string;
  viajes: number;
  /** `Money` canónico: pásalo por `formatearCOP`. */
  ingresos: string;
  ingresosNum: number;
  /** Suma de cantidades (kg…) o `null` si la ruta no lleva cantidad. */
  cantidad: number | null;
  /** Ingreso promedio por viaje. */
  porViaje: number;
  /** Ingreso por unidad (p. ej. $/kg) o `null` sin cantidad. */
  porUnidad: number | null;
  /** Ingresos cobrados distinto de lo que proponía la tarifa. */
  ajustados: number;
}

export function ingresosPorRuta(revenues: Revenue[]): RutaMetrica[] {
  const grupos = new Map<string, Revenue[]>();
  // La ruta es el NOMBRE de la tarifa dentro del cliente, no su id: subir
  // precios crea otra fila de tarifa (vigencia nueva) y la ruta sigue siendo
  // la misma.
  for (const r of revenues) {
    const k = r.tariffName ? `${r.customerId}|${r.tariffName}` : `sin-tarifa:${r.customerId}`;
    const g = grupos.get(k);
    if (g) g.push(r); else grupos.set(k, [r]);
  }
  const filas: RutaMetrica[] = [];
  for (const [key, lista] of grupos) {
    const primero = lista[0];
    const ingresos = sumarMontos(lista.map(r => r.amount));
    const ingresosNum = Number(ingresos);
    const conCantidad = lista.filter(r => r.quantity && Number(r.quantity) > 0);
    const cantidad = conCantidad.length > 0 ? conCantidad.reduce((s, r) => s + Number(r.quantity), 0) : null;
    // Una tarifa de precio fijo no tiene unidad, pero el ingreso puede llevar
    // cantidad (los kg del tiquete): el $/unidad sigue siendo útil, sin sufijo.
    const unit = lista.map(r => r.tariffSnapshot?.unit as string | null | undefined).find(u => !!u) ?? undefined;
    filas.push({
      key,
      ruta: primero.tariffName ?? 'Sin tarifa (importe manual)',
      cliente: primero.customerName,
      clienteId: primero.customerId,
      unit,
      viajes: lista.length,
      ingresos,
      ingresosNum,
      cantidad,
      porViaje: ingresosNum / lista.length,
      porUnidad: cantidad ? ingresosNum / cantidad : null,
      ajustados: lista.filter(cobradoDistintoDelPropuesto).length,
    });
  }
  return filas.sort((a, b) => b.ingresosNum - a.ingresosNum);
}
